import { useIsSignedUrlPending, useSignedUrl } from '~/features/signed-urls/signed-urls'
import React from 'react'
import { twMerge } from 'tailwind-merge'

export type ImageFit = 'cover' | 'contain' | 'fill' | 'none' | 'scale-down'
export type ImageShape = 'rounded' | 'rectangle' | 'circle'

export type ImgProps = {
  src?: string | undefined
  srcSet?: string | undefined
  sizes?: string | undefined
  alt?: string
  className?: string
  fit?: ImageFit
  shape?: ImageShape
  style?: React.CSSProperties
  onError?: React.ReactEventHandler<HTMLImageElement> | undefined
}

export const Img = React.forwardRef<HTMLImageElement, ImgProps>(
  ({ src, srcSet, sizes, alt, className, fit = 'cover', shape = 'rectangle', style, onError }, ref) => {
    const signedUrl = useSignedUrl(src)
    const isSignedUrlPending = useIsSignedUrlPending(src)
    const fitClasses = {
      cover: 'object-cover object-center',
      contain: 'object-contain',
      fill: 'object-fill',
      none: 'object-none',
      'scale-down': 'object-scale-down'
    }

    const shapeClasses = {
      rounded: 'rounded-sm',
      rectangle: 'rounded-none',
      circle: 'rounded-full'
    }

    // A pending file is recorded but not in storage yet, so its unsigned url would just 403.
    if (isSignedUrlPending) {
      return (
        <div
          className={twMerge(
            'w-full h-full flex items-center justify-center border border-dashed border-gray-300 bg-gray-50 px-2 text-center text-xs text-gray-500',
            shapeClasses[shape],
            className
          )}
          style={style}
        >
          Still uploading
        </div>
      )
    }

    return (
      <img
        ref={ref}
        src={signedUrl}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        className={twMerge('w-full h-full', fitClasses[fit], shapeClasses[shape], className)}
        style={style}
        onError={onError}
      />
    )
  }
)

Img.displayName = 'Image'
